import { z } from "zod";
import { optionalNumber } from "@schemas/common";

// =============================================================================
// CALCULAR ALQUILER FORM SCHEMA
// =============================================================================

export const calcularAlquilerSchema = z
	.object({
		// Location — resolved by LocationPicker, passed separately; distrito is the
		// only field validated here since it is required by the backend.
		distrito: z
			.string({ required_error: "required" })
			.min(1, "required"),

		// Unit attributes
		amoblado: z.enum(["si", "no"]).default("no"),
		antiguedad_anos: optionalNumber.refine(
			(val) => val === undefined || val >= 0,
			{ message: "non_negative" },
		),
		area_total: z.coerce
			.number({ required_error: "required_positive", invalid_type_error: "required_positive" })
			.positive("required_positive"),
		area_construida: z.coerce
			.number({ required_error: "required_positive", invalid_type_error: "required_positive" })
			.positive("required_positive"),
		nivel_piso: optionalNumber.refine(
			(val) => val === undefined || val >= 1,
			{ message: "non_negative" },
		),

		// Stepper fields — always integers ≥ 0
		dormitorios: z.coerce.number().int().min(0).default(1),
		banos: z.coerce.number().int().min(0).default(1),
		estacionamiento: z.coerce.number().int().min(0).default(0),

		// Boolean flags — all default false (unchecked)
		sin_ascensor: z.boolean().default(false),
		sin_vista: z.boolean().default(false),
		is_duplex: z.boolean().default(false),
	})
	.superRefine((data, ctx) => {
		if (data.area_construida > data.area_total) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["area_construida"],
				message: "superficie_construida_exceeds_total",
			});
		}
	});

export type CalcularAlquilerInput = z.infer<typeof calcularAlquilerSchema>;
/**
 * What the form holds before the schema runs. Fields with `.default()` are
 * optional here and required in `CalcularAlquilerInput`, which is why useForm
 * needs both: `useForm<Values, unknown, CalcularAlquilerInput>`.
 */
export type CalcularAlquilerValues = z.input<typeof calcularAlquilerSchema>;


export type CalcularAlquilerData = z.output<typeof calcularAlquilerSchema>;

export const DEFAULT_CALCULAR_ALQUILER_VALUES: CalcularAlquilerInput = {
	distrito: "",
	amoblado: "no",
	antiguedad_anos: undefined,
	area_total: "" as unknown as number,
	area_construida: "" as unknown as number,
	nivel_piso: undefined,
	dormitorios: 1,
	banos: 1,
	estacionamiento: 0,
	sin_ascensor: false,
	sin_vista: false,
	is_duplex: false,
};
